/**
 * Athena Workflow Configuration
 * Centralized configuration for all workflow-related constants and settings
 */

export const WORKFLOW_TIMINGS = {
  // Polling intervals
  POLLING_INTERVAL: 5000,           // 5 seconds between status checks
  INITIAL_POLLING_DELAY: 2000,      // 2 seconds before first poll

  // Timeouts
  UPLOAD_TIMEOUT: 300000,           // 5 minutes for document upload
  AGENT_POLLING_TIMEOUT: 0,         // No timeout - poll indefinitely
  REVISION_TIMEOUT: 300000,         // 5 minutes for revision requests
  DEFAULT_REQUEST_TIMEOUT: 60000,   // 1 minute default

  // Delays
  REVISION_PROCESSING_DELAY: 3000,  // 3 seconds after revision submission
  RATE_LIMIT_COOLDOWN: 5000,        // 5 seconds cooldown for rate limits
  UI_TRANSITION_DELAY: 300,         // 300ms for UI transitions
  MESSAGE_DISPLAY_DELAY: 2000,      // 2 seconds for message display

  // Retry configuration
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_BACKOFF_MULTIPLIER: 1.5,
  INITIAL_RETRY_DELAY: 1000,
};

export const WORKFLOW_STATES = {
  // Initial states
  IDLE: 'idle',
  INITIALIZING: 'initializing',

  // Document handling
  UPLOADING_DOCUMENT: 'uploading_document',
  DOCUMENT_UPLOADED: 'document_uploaded',

  // Analysis states
  STARTING_ANALYSIS: 'starting_analysis',
  PROCESSING: 'processing',
  POLLING: 'polling',

  // Review states
  REVIEWING: 'reviewing',
  AWAITING_APPROVAL: 'awaiting_approval',
  APPROVED: 'approved',

  // Revision states
  REVISION_REQUESTED: 'revision_requested',
  REVISING: 'revising',
  REVISION_POLLING: 'revision_polling',

  // Transition states
  TRANSITIONING_STEP: 'transitioning_step',
  WAITING_NEXT_STEP: 'waiting_next_step',

  // Completion states
  STEP_COMPLETED: 'step_completed',
  ANALYSIS_COMPLETED: 'analysis_completed',

  // Error states
  ERROR: 'error',
  RATE_LIMITED: 'rate_limited',
  TIMEOUT: 'timeout',
  PAUSED: 'paused'
};

export const ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  RATE_LIMIT: 'RATE_LIMIT',

  // API errors
  INVALID_REQUEST: 'INVALID_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',

  // Workflow errors
  INVALID_STATE: 'INVALID_STATE',
  MISSING_PREREQUISITES: 'MISSING_PREREQUISITES',
  AGENT_FAILURE: 'AGENT_FAILURE',
  REVISION_FAILURE: 'REVISION_FAILURE',

  // Data errors
  INVALID_DATA: 'INVALID_DATA',
  MISSING_DATA: 'MISSING_DATA',
  VALIDATION_ERROR: 'VALIDATION_ERROR'
};

export const STORAGE_KEYS = {
  CONVERSATIONS: 'athena_conversations',
  ACTIVE_CONVERSATION_ID: 'athena_active_conversation_id',
  USER_PREFERENCES: 'athena_user_preferences',
  DRAFT_DATA: 'athena_draft_data'
};

export const LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024,  // 10MB
  MAX_FILES_PER_UPLOAD: 5,
  MAX_CONVERSATION_AGE_DAYS: 30,
  MAX_STORED_CONVERSATIONS: 50,
  MAX_MESSAGE_LENGTH: 5000,
  MAX_FEEDBACK_LENGTH: 2000
};

export const SUPPORTED_FILE_TYPES = {
  DOCUMENTS: ['.pdf', '.docx', '.doc', '.txt', '.rtf', '.md'],
  IMAGES: ['.png', '.jpg', '.jpeg', '.gif', '.bmp'],
  SPREADSHEETS: ['.xlsx', '.xls', '.csv'],
  PRESENTATIONS: ['.pptx', '.ppt']
};

export const STEP_CONFIGURATION = {
  1: {
    name: 'Item Definition',
    requiredFields: ['itemName', 'itemDescription'],
    validationRules: {
      itemName: { minLength: 3, maxLength: 100 },
      itemDescription: { minLength: 10, maxLength: 2000 }
    }
  },
  2: {
    name: 'Asset Identification',
    requiredFields: ['assets'],
    validationRules: {
      minAssets: 1,
      maxAssets: 100
    }
  },
  3: {
    name: 'Damage Scenarios',
    requiredFields: ['damageScenarios'],
    validationRules: {
      minScenarios: 1,
      maxScenarios: 150
    }
  },
  4: {
    name: 'Threat Identification',
    requiredFields: ['threats'],
    validationRules: {
      minThreats: 1,
      maxThreats: 200
    }
  },
  5: {
    name: 'Attack Path Feasibility',
    requiredFields: ['attackPaths'],
    validationRules: {
      minPaths: 1,
      maxPaths: 100
    }
  },
  6: {
    name: 'Risk Assessment',
    requiredFields: ['risks'],
    validationRules: {
      minRisks: 1,
      maxRisks: 150
    }
  },
  7: {
    name: 'Cybersecurity Goals',
    requiredFields: ['cybersecurityGoals', 'cybersecurityClaims'],
    validationRules: {
      minGoals: 0,
      maxGoals: 100
    }
  },
  8: {
    name: 'Cybersecurity Concept',
    requiredFields: ['cybersecurityControls', 'cybersecurityRequirements'],
    validationRules: {
      minControls: 0,
      maxControls: 200,
      minRequirements: 0,
      maxRequirements: 300
    }
  },
  9: {
    name: 'Final Report',
    requiredFields: ['report'],
    validationRules: {}
  },
  10: {
    name: 'Risk Management',
    description: 'Impact analysis and residual risk assessment',
    requiredFields: [],
    validationRules: {
      // No validation rules - this is a separate workflow phase
    }
  }
};

// Helper function to get timing with optional override
export const getTiming = (key, override = null) => {
  return override !== null ? override : WORKFLOW_TIMINGS[key];
};

// Helper function to check if error is recoverable
export const isRecoverableError = (errorCode) => {
  const recoverableErrors = [
    ERROR_CODES.NETWORK_ERROR,
    ERROR_CODES.TIMEOUT,
    ERROR_CODES.RATE_LIMIT,
    ERROR_CODES.SERVER_ERROR
  ];
  return recoverableErrors.includes(errorCode);
};

// Helper function to get retry delay with exponential backoff
export const getRetryDelay = (attemptNumber) => {
  const baseDelay = WORKFLOW_TIMINGS.INITIAL_RETRY_DELAY;
  const multiplier = WORKFLOW_TIMINGS.RETRY_BACKOFF_MULTIPLIER;
  return Math.min(
    baseDelay * Math.pow(multiplier, attemptNumber - 1),
    30000 // Max 30 seconds
  );
};